import { useMemo, useState } from "react";
import { UserMinus, UserPlus, Search } from "lucide-react";
import { useSearchStudents } from "../../query/parentQuery";
import { useUpdateChild } from "../../query/childQuery";
import { UPLOADS_URL } from "../../config/constants";
import swal from "../../utils/swal";
import "../../views/teachers/addTeacher.css";

function childName(child) {
  return `${child?.firstName || ""} ${child?.lastName || ""}`.trim() || "Student";
}

function childAvatar(child) {
  if (!child?.image) return null;
  if (String(child.image).startsWith("blob:") || String(child.image).startsWith("http")) {
    return child.image;
  }
  return `${UPLOADS_URL}/${child.image}`;
}

/**
 * Assign free students to a parent, or resign currently assigned ones.
 * @param {string} [parentId] - required for live API assign/resign (edit mode)
 * @param {array} assignedChildren - currently linked children (from parent record)
 * @param {array} stagedChildren - free children staged for assign-on-create
 * @param {function} onStagedChange - update staged children (add mode)
 */
export default function ParentChildrenAssign({
  parentId,
  assignedChildren = [],
  stagedChildren = [],
  onStagedChange,
  disabled = false,
}) {
  const [keyword, setKeyword] = useState("");
  const { data: students = [], isFetching } = useSearchStudents(
    keyword.trim() ? { keyword: keyword.trim() } : {}
  );
  const { mutate: updateChild, isPending } = useUpdateChild();
  const isLive = Boolean(parentId);

  const assigned = isLive ? assignedChildren || [] : stagedChildren || [];

  const freeStudents = useMemo(() => {
    const blocked = new Set(
      [...(assignedChildren || []), ...(stagedChildren || [])].map((c) => String(c._id))
    );
    return (students || []).filter((s) => {
      if (blocked.has(String(s._id))) return false;
      return !s.parent;
    });
  }, [students, assignedChildren, stagedChildren]);

  const setParentOnChild = (childId, nextParentId, successMsg) => {
    const formObject = new FormData();
    formObject.append("parent", nextParentId || "");
    updateChild(
      { childId, data: formObject },
      {
        onSuccess: () => swal("Success", successMsg, "success"),
        onError: (error) =>
          swal("Error", error?.message || "Could not update assignment", "error"),
      }
    );
  };

  const assignChild = (child) => {
    if (disabled || isPending) return;
    if (!isLive) {
      if (stagedChildren.some((c) => String(c._id) === String(child._id))) return;
      onStagedChange?.([...stagedChildren, child]);
      return;
    }
    setParentOnChild(child._id, parentId, `${childName(child)} assigned to this parent`);
  };

  const resignChild = (child) => {
    if (disabled || isPending) return;
    if (!isLive) {
      onStagedChange?.(
        stagedChildren.filter((c) => String(c._id) !== String(child._id))
      );
      return;
    }
    swal({
      title: "Resign student?",
      text: `${childName(child)} will be unassigned and become available again.`,
      icon: "warning",
      buttons: ["Cancel", "Resign"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setParentOnChild(child._id, "", `${childName(child)} removed from this parent`);
    });
  };

  return (
    <section className="tf-card">
      <h2 className="tf-card-head">Children</h2>
      <div className="tf-card-body tf-children-body">
        <p className="tf-hint tf-children-intro">
          {isLive
            ? "Assign unassigned students to this parent, or resign a linked student so they become free again."
            : "Pick free students to link after you create this parent. You can change assignments anytime from edit."}
        </p>

        <div className="tf-field is-full">
          <p className="tf-label">Assigned children</p>
          {assigned.length === 0 ? (
            <div className="tf-empty-chip">No children assigned yet</div>
          ) : (
            <ul className="tf-child-list">
              {assigned.map((child) => (
                <li key={child._id} className="tf-child-row">
                  <div className="tf-child-meta">
                    <div className="tf-child-avatar">
                      {childAvatar(child) ? (
                        <img src={childAvatar(child)} alt="" />
                      ) : (
                        <span>{(child.firstName || "?").slice(0, 1)}</span>
                      )}
                    </div>
                    <div>
                      <p className="tf-child-name">{childName(child)}</p>
                      <p className="tf-child-sub">
                        {child.rollNumber ? `Roll ${child.rollNumber}` : "No roll number"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tf-chip-btn is-danger"
                    disabled={disabled || isPending}
                    onClick={() => resignChild(child)}
                  >
                    <UserMinus size={14} />
                    Resign
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="tf-field is-full">
          <p className="tf-label">Add free student</p>
          <div className="tf-search-row">
            <Search size={16} className="tf-search-icon" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search by name or roll number"
              disabled={disabled}
            />
          </div>
          {isFetching ? (
            <p className="tf-hint">Searching…</p>
          ) : freeStudents.length === 0 ? (
            <div className="tf-empty-chip">
              {keyword.trim()
                ? "No free students match that search"
                : "No free (unassigned) students available"}
            </div>
          ) : (
            <ul className="tf-child-list">
              {freeStudents.slice(0, 12).map((child) => (
                <li key={child._id} className="tf-child-row">
                  <div className="tf-child-meta">
                    <div className="tf-child-avatar is-muted">
                      <span>{(child.firstName || "?").slice(0, 1)}</span>
                    </div>
                    <div>
                      <p className="tf-child-name">{childName(child)}</p>
                      <p className="tf-child-sub">
                        {child.rollNumber ? `Roll ${child.rollNumber}` : "Free student"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tf-chip-btn is-primary"
                    disabled={disabled || isPending}
                    onClick={() => assignChild(child)}
                  >
                    <UserPlus size={14} />
                    Assign
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
